import { useEffect, useRef, useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useClerk, useOrganization } from "@clerk/clerk-react";
import { useCurrentUser, userDisplayName, userInitials, usePlatformAdmin } from "./AuthProvider";
import { useActiveWorkspace, useEnsureActiveWorkspace } from "@/lib/useActiveWorkspace";
import { useWorkspaceContext } from "@/lib/workspace-context";
import { useApiQuery } from "@/lib/api";
import { canSeeNewsletter } from "@/lib/newsletter-access";
import { canSeeSettings, type SettingsScreen } from "@/lib/roles";
import { useRole } from "@/lib/useRole";
import { Icon } from "./Icon";

/**
 * Sidebar — the persistent left rail, replacing the old horizontal TopBar
 * as the app's primary chrome (FEAT-087, Phase 1).
 *
 * Anatomy and every value here come from the approved design file
 * (`design_handoff_nagareru_redesign/nagareru - Redesign.dc.html`), read
 * directly rather than from the handoff README's prose — the two disagree
 * in two places and the comp wins per the handoff's own instruction:
 *   - README says nav rows carry a "6px stage-colour dot"; the comp draws
 *     an 18px pictogram + label + count badge. Built to the comp.
 *   - README says "6px radius, 10px 12px padding" for nav rows; the comp
 *     uses 7px radius and 9px 10px. Built to the comp.
 *
 * Two deliberate departures from the design, both temporary and both
 * about not regressing shipped behavior ahead of the phases that cover it:
 *   - The design's account row navigates straight to Settings. Here it
 *     keeps the TopBar-era popover, because that popover is currently the
 *     only route to Sign out — the design moves sign-out into
 *     Settings → Account, which Phase 6 builds. Collapse this to a plain
 *     link then.
 *   - Nav counts are omitted rather than faked. The design derives them
 *     (Desk = stopped + ideas); that query arrives with the Desk queue in
 *     Phase 2, and the handoff is explicit that every count is derived,
 *     never typed.
 *
 * The Platform-admin group has no counterpart in the design (Admin is
 * explicitly out of scope there) and is carried over from TopBar so those
 * routes stay reachable.
 */

interface NavEntry {
  to: string;
  label: string;
  icon: Parameters<typeof Icon>[0]["name"];
}

// Founder change to the design, 2026-09-05: the approved comp has one Desk
// row with Queue and Board behind an in-screen selector. In use that read
// wrong — switching between two primary ways of seeing all your work is a
// navigation act, so it belongs in the nav, not inside one of the screens
// it switches between. Queue and Board are peer entries here instead.
const WORKSPACE_NAV: NavEntry[] = [
  { to: "/desk", label: "Queue", icon: "desk" },
  { to: "/desk/board", label: "Board", icon: "board" },
  { to: "/calendar", label: "Calendar", icon: "calendar-nav" },
  { to: "/social", label: "Social", icon: "social" },
  { to: "/analytics", label: "Analytics", icon: "analytics" },
];

interface SidebarProps {
  onOpenSettings?: () => void;
  onSignOut?: () => void;
}

export function Sidebar({ onOpenSettings, onSignOut }: SidebarProps) {
  const { user } = useCurrentUser();
  const { organization, membership } = useOrganization();
  const isAdmin = !!organization && membership?.role === "org:admin";
  const isPlatformAdmin = usePlatformAdmin();
  // FEAT-096 — same query key as AccountSettingsView, so one fetch serves both.
  const { data: tenantMe } = useApiQuery<{ tenant_type?: string }>(["tenants", "me"], "/tenants/me");
  const showNewsletter = canSeeNewsletter(tenantMe?.tenant_type);

  return (
    <aside className="sidebar">
      <div className="sidebar-brand">
        <p className="sidebar-wordmark">nagareru</p>
      </div>

      <SidebarWorkspacePicker />

      <span className="sidebar-group">Workspace</span>
      <nav className="sidebar-nav" aria-label="Workspace">
        {WORKSPACE_NAV.map((item) => (
          <NavLink
            key={item.to}
            to={item.to}
            end
            title={item.label}
            className={({ isActive }) => `sidebar-nav-item ${isActive ? "is-active" : ""}`}
          >
            <Icon name={item.icon} size={18} stroke={1.6} />
            <span className="sidebar-nav-label">{item.label}</span>
          </NavLink>
        ))}
        {showNewsletter && (
          <NavLink
            to="/newsletter"
            end
            title="Newsletter"
            className={({ isActive }) => `sidebar-nav-item ${isActive ? "is-active" : ""}`}
          >
            <Icon name="book" size={18} stroke={1.6} />
            <span className="sidebar-nav-label">Newsletter</span>
          </NavLink>
        )}
      </nav>

      {isPlatformAdmin && (
        <>
          <span className="sidebar-group">Platform admin</span>
          <nav className="sidebar-nav" aria-label="Platform admin">
            <NavLink
              to="/admin/analytics"
              end
              title="Admin"
              className={({ isActive }) => `sidebar-nav-item ${isActive ? "is-active" : ""}`}
            >
              <Icon name="analytics" size={18} stroke={1.6} />
              <span className="sidebar-nav-label">Admin</span>
            </NavLink>
            <NavLink
              to="/admin/tenants"
              end
              title="Tenants"
              className={({ isActive }) => `sidebar-nav-item ${isActive ? "is-active" : ""}`}
            >
              <Icon name="globe" size={18} stroke={1.6} />
              <span className="sidebar-nav-label">Tenants</span>
            </NavLink>
            <NavLink
              to="/admin/audit-log"
              end
              title="Audit Log"
              className={({ isActive }) => `sidebar-nav-item ${isActive ? "is-active" : ""}`}
            >
              <Icon name="doc" size={18} stroke={1.6} />
              <span className="sidebar-nav-label">Audit Log</span>
            </NavLink>
          </nav>
        </>
      )}

      <div className="sidebar-spacer" />

      <SidebarAccount
        name={user ? userDisplayName(user) : ""}
        initials={userInitials(user)}
        isAdmin={isAdmin}
        onOpenSettings={onOpenSettings}
        onSignOut={onSignOut}
      />
    </aside>
  );
}

/**
 * The workspace-identity chip — a real agencyclient picker, not just a
 * label. Visually it's the avatar+name+caret chip the design calls for; a
 * transparent, full-size native `<select>` sits on top of it, so opening
 * it gets the platform's real dropdown/keyboard/screen-reader behavior
 * instead of a hand-rolled popover reimplementing all of that.
 */
function SidebarWorkspacePicker() {
  const { workspaces, isLoading, setSelectedId, activeWorkspace, allWorkspaces, pinnedId } =
    useWorkspaceContext();
  const { activate, isActivating, error, clearError } = useActiveWorkspace();
  const [pendingId, setPendingId] = useState<number | null>(null);

  // BUG-068 — skip the sync while "All workspaces" is showing. In that mode
  // `activeWorkspace` falls back to workspaces[0], so syncing it would
  // silently repoint the real backend/JWT active workspace at whichever
  // workspace happens to sort first, regardless of what was active before.
  // The hook treats undefined as a no-op.
  useEnsureActiveWorkspace(allWorkspaces ? undefined : activeWorkspace?.id);

  if (isLoading || workspaces.length === 0) return null;

  async function handleChange(nextId: number) {
    if (nextId === 0 || nextId === activeWorkspace?.id) {
      setSelectedId(nextId);
      return;
    }
    clearError();
    setPendingId(nextId);
    try {
      await activate(nextId);
      setSelectedId(nextId);
    } catch {
      // surfaced via `error` below
    } finally {
      setPendingId(null);
    }
  }

  const displayName = allWorkspaces ? "All workspaces" : (activeWorkspace?.name ?? "");

  // BUG-137: a piece belongs to one workspace, so while one is open the chip
  // names it and offers no switch; leaving the piece brings the picker back.
  if (pinnedId != null) {
    return (
      <div className="sidebar-ws sidebar-ws--pinned" title="This piece's workspace. Leave the piece to switch.">
        <span className="sidebar-ws-avatar">{displayName.slice(0, 1).toUpperCase()}</span>
        <span className="sidebar-ws-name">{isActivating ? "Switching…" : displayName}</span>
      </div>
    );
  }

  return (
    <div className="sidebar-ws">
      <select
        className="sidebar-ws-select"
        aria-label="Workspace"
        value={allWorkspaces ? 0 : (pendingId ?? activeWorkspace?.id ?? "")}
        disabled={isActivating}
        onChange={(e) => void handleChange(Number(e.target.value))}
      >
        {workspaces.length > 1 && <option value={0}>All workspaces</option>}
        {workspaces.map((w) => (
          <option key={w.id} value={w.id}>
            {w.name} ({w.slug})
          </option>
        ))}
      </select>
      <span className="sidebar-ws-avatar">{displayName.slice(0, 1).toUpperCase()}</span>
      <span className="sidebar-ws-name">{isActivating ? "Switching…" : displayName}</span>
      <span className="sidebar-ws-caret" aria-hidden>
        ▾
      </span>
      {error && !isActivating && (
        <span className="sidebar-ws-error">
          {error.status === 403
            ? "No access to that workspace."
            : (error.message ?? "Could not switch.")}
        </span>
      )}
    </div>
  );
}

interface SidebarAccountProps {
  name: string;
  initials: string;
  isAdmin: boolean;
  onOpenSettings?: () => void;
  onSignOut?: () => void;
}

const SETTINGS_SHORTCUTS: { screen: SettingsScreen; label: string }[] = [
  { screen: "publications", label: "Publications" },
  { screen: "publish-targets", label: "Publish targets" },
  { screen: "team", label: "Team" },
];

/**
 * The account row, pinned to the footer. Clicking opens a popover with
 * "All settings" emphasized, the three most-visited settings routes, then
 * Account / Sign out — carried over from the TopBar-era AccountMenu so
 * signing out stays reachable (see the component header note).
 */
function SidebarAccount({
  name,
  initials,
  isAdmin,
  onOpenSettings,
  onSignOut,
}: SidebarAccountProps) {
  const clerk = useClerk();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [confirmSignOut, setConfirmSignOut] = useState(false);
  // FEAT-108 / S-437 — only the shortcuts this member's role may open.
  const { role } = useRole();
  const shortcuts = SETTINGS_SHORTCUTS.filter((s) => canSeeSettings(s.screen, role));
  const wrapRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDoc(e: MouseEvent) {
      if (wrapRef.current && !wrapRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent) {
      if (e.key === "Escape") setOpen(false);
    }
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  async function doSignOut() {
    setConfirmSignOut(false);
    const handler = onSignOut ?? (() => clerk.signOut());
    await handler();
    navigate("/login", { replace: true });
  }

  function goSettings(path: string) {
    setOpen(false);
    navigate(path);
  }

  return (
    <div className="sidebar-account" ref={wrapRef}>
      <button
        className="sidebar-account-btn"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="menu"
        aria-expanded={open}
      >
        <span className="sidebar-account-avatar">{initials}</span>
        <span className="sidebar-account-id">
          <span className="sidebar-account-name">{name}</span>
          <span className="sidebar-account-sub">{isAdmin ? "admin · settings" : "settings"}</span>
        </span>
        <span className="sidebar-account-caret" aria-hidden>
          {open ? "▾" : "▴"}
        </span>
      </button>

      {open && (
        <div className="sidebar-popover" role="menu">
          <button
            className="sidebar-popover-item is-emphasized"
            role="menuitem"
            onClick={() =>
              onOpenSettings ? (setOpen(false), onOpenSettings()) : goSettings("/settings")
            }
          >
            All settings
          </button>
          {shortcuts.map((s) => (
            <button
              key={s.screen}
              className="sidebar-popover-item"
              role="menuitem"
              onClick={() => goSettings(`/settings/${s.screen}`)}
            >
              {s.label}
            </button>
          ))}
          {shortcuts.length > 0 && <div className="sidebar-popover-div" />}
          <button
            className="sidebar-popover-item"
            role="menuitem"
            onClick={() => goSettings("/settings/account")}
          >
            Account
          </button>
          <div className="sidebar-popover-div" />
          <button
            className="sidebar-popover-item is-danger"
            role="menuitem"
            onClick={() => {
              setOpen(false);
              setConfirmSignOut(true);
            }}
          >
            Sign out
          </button>
        </div>
      )}

      {confirmSignOut && (
        <div
          className="rev-overlay"
          onMouseDown={(e) => {
            if (e.target === e.currentTarget) setConfirmSignOut(false);
          }}
        >
          <div className="rev-modal" role="dialog" aria-labelledby="so-title" style={{ maxWidth: 400 }}>
            <header className="rev-head">
              <div className="rev-head-l">
                <div className="rev-eyebrow">Confirm</div>
                <h2 id="so-title" className="rev-title">
                  Sign out of nagareru?
                </h2>
              </div>
              <button className="rev-x" onClick={() => setConfirmSignOut(false)} aria-label="Close">
                ×
              </button>
            </header>
            <div className="rev-body">
              <p className="rev-lede">
                You're signed in as <strong>{name}</strong>. You'll return to the login screen and
                need to sign in again to continue.
              </p>
            </div>
            <footer className="rev-foot">
              <div className="rev-foot-l" />
              <div className="rev-actions">
                <button className="rev-btn rev-btn-ghost" onClick={() => setConfirmSignOut(false)}>
                  Cancel
                </button>
                <button className="rev-btn rev-btn-danger" onClick={() => void doSignOut()}>
                  Sign out
                </button>
              </div>
            </footer>
          </div>
        </div>
      )}
    </div>
  );
}
